'use client';

import { useMemo } from 'react';

import { Column, Rows } from '@bytel/trilogy-react';

import { FundingEnumType } from '@app-types/funding';
import { MobilePlanType } from '@app-types/plan';
import { Product } from '@app-types/product';
import { UserType } from '@app-types/user';

import { MarketingsState } from '@hooks/use-marketings';

import { getCurrentPhone } from '@helpers/cart';
import { getSessionFunding, isComptant, isEDP } from '@helpers/funding';

import { PlanRange } from '@constants/plan';

import { OfferCard, PhoneInCart } from '@home-mobile/components';
import { PricesDetails } from '@product-detail/helpers';

type OfferCardContainerProps = {
    user?: UserType;
    plan: MobilePlanType & { priceDetails?: PricesDetails };
    openOfferDetails: (offer: MobilePlanType) => void;
    phoneInCart?: Product;
    isRenewal?: boolean;
    onAddToCart: (chosenOffer: MobilePlanType, fundingKey?: string) => Promise<void>;
    isConnected?: boolean;
    marketingDiscounts: MarketingsState;
    isSmallPrice: boolean;
    hatInLine: boolean;
    showBlockedPlanZone: boolean;
};

export function OfferCardContainer({
    user,
    plan,
    openOfferDetails,
    phoneInCart,
    isRenewal = false,
    onAddToCart,
    isConnected = false,
    marketingDiscounts,
    isSmallPrice,
    hatInLine,
    showBlockedPlanZone,
}: OfferCardContainerProps) {
    const fundings = getCurrentPhone(plan.simulatedCart)?.details.fundings;
    const funding = useMemo(() => {
        return fundings?.find((f) => {
            const fundingStorage = getSessionFunding();
            return fundingStorage ? f.type === fundingStorage : f.favorite;
        });
    }, [plan.simulatedCart]);

    const younitedFunding = useMemo(() => {
        return fundings?.find((funding) => funding.type === FundingEnumType.YOUNITED_X24);
    }, [plan.simulatedCart]);

    const edpFunding = useMemo(() => {
        return fundings?.find((funding) => isEDP(funding.type));
    }, [plan.simulatedCart]);

    const cashFunding = useMemo(() => {
        return fundings?.find((funding) => isComptant(funding.type));
    }, [plan.simulatedCart]);

    const getFundingToShow = () => {
        if (isRenewal) {
            const fundingKey = getSessionFunding();
            if (plan.isOwned) {
                const selectedFunding = fundings?.find((funding) => funding.type === fundingKey);
                return selectedFunding ?? fundings?.find((funding) => funding.favorite);
            }
            return fundings?.find((funding) => funding.favorite);
        }
        return funding || younitedFunding || edpFunding || cashFunding;
    };

    return (
        <Column className={isRenewal ? 'is-fullheight' : ''} narrow={isRenewal} size={isRenewal ? 12 : 4} id={plan.id}>
            <Rows>
                {phoneInCart &&
                    plan?.details?.price &&
                    (plan.commercialRange === PlanRange.WITH_MOBILE || isRenewal) && (
                        <PhoneInCart phone={phoneInCart} plan={plan} selectedFunding={getFundingToShow()} />
                    )}
                <OfferCard
                    user={user}
                    offer={plan}
                    openOfferDetails={openOfferDetails}
                    isRenewal={isRenewal}
                    hasPhoneInCart={!!phoneInCart}
                    onAddToCart={onAddToCart}
                    isConnected={isConnected}
                    marketingDiscounts={marketingDiscounts}
                    isSmallPrice={isSmallPrice}
                    showBlockedPlanZone={showBlockedPlanZone}
                    hatInLine={hatInLine}
                    selectedFunding={getFundingToShow()}
                />
            </Rows>
        </Column>
    );
}
